<template>
  <div>
    <ul class="list-unstyled mb-0">
      <template v-for="node in showValues">
        <tree-row
          :depth="0"
          :key="node.name"
          :node="node"
          :range="range"
          :ranges="ranges"
          :all="showValues"
          :has-nested="hasNestedOptions"
          @emit-node-checked="onNodeChecked"
        >
          <template
            v-for="slot in Object.keys($scopedSlots)"
            v-slot:[slot]="scope"
          >
            <slot :name="slot" v-bind="scope" />
          </template>
        </tree-row>
      </template>
    </ul>
    <button
      v-if="preparedValues.length > showCount"
      class="btn-link-alt"
      @click.stop="showCount += 20"
    >
      {{ $t('showMore') }}
      {{
        $t('outOf', {
          out: canShowMoreCount,
          of: this.preparedValues.length - this.showCount
        })
      }}
    </button>
  </div>
</template>

<script>
import TreeRow from './TreeRow.vue';
import cloneDeep from 'clone';
import isEqual from 'js-isequal';
import debounce from 'debounce';

export default {
  name: 'Tree',
  props: {
    options: {
      type: Array,
      required: true
    },
    value: {
      type: Array,
      default: () => []
    },
    uniqKey: {
      type: String,
      default: 'name'
    }
  },
  components: {
    TreeRow: TreeRow
  },

  data() {
    return {
      preparedValues: [],
      showCount: 20,
      currentOptions: null,
      currentValue: null,
      range: false,
      ranges: {
        first: {
          value: null,
          state: null
        },
        second: null
      }
    };
  },

  computed: {
    hasNestedOptions() {
      let hasNestedOptions = false;
      for (let i = 0; i < this.options.length; i++) {
        if (this.options[i].values) {
          hasNestedOptions = true;
          break;
        }
      }

      return hasNestedOptions;
    },
    showValues() {
      return this.preparedValues.slice(0, this.showCount);
    },
    canShowMoreCount() {
      const count = this.preparedValues.length - this.showCount;
      return count >= 20 ? 20 : count;
    }
  },

  methods: {
    init() {
      this.initValues();
    },
    initValues() {
      this.preparedValues = this.prepareData();
      const findByProp = (tree, predicate, notFound) => {
        if (predicate(tree)) return tree;
        else if (typeof notFound === 'function') notFound(tree);
        if (tree.values) {
          for (let i = 0; i < tree.values.length; i++) {
            const exist = findByProp(tree.values[i], predicate);
            if (exist && predicate(exist)) return exist;
          }
        }
        return null;
      };

      this.value.forEach(el => {
        const activeValue = findByProp(
          { values: this.preparedValues },
          preparedEl => el[this.uniqKey] === preparedEl[this.uniqKey]
        );
        this.$nextTick(() => {
          if (activeValue)
            this.onNodeChecked({ node: activeValue, newState: true });
        });
      });
    },
    emitInput() {
      const spliceCheckedValues = tree => {
        return tree.reduce((list, entry) => {
          let clone = null;
          let childChecked = [...list];
          if (entry.state.checked) {
            clone = entry;
            delete clone.values;
            delete clone.state;
          } else if (entry.state.indeterminate) {
            childChecked = [
              ...childChecked,
              ...spliceCheckedValues(entry.values)
            ];
          }
          clone && childChecked.push(clone);

          return childChecked;
        }, []);
      };

      this.$emit('input', spliceCheckedValues(cloneDeep(this.preparedValues)));
    },
    onNodeChecked({ node, newState, ranges }) {
      if (node.state.checked === newState) return;
      node.state.checked = newState;
      this.syncNestedValues(node, newState);
      this.syncParentValues();

      if (this.range) {
        if (ranges.first.value !== null && ranges.second !== null) {
          const first =
            ranges.first.value > ranges.second
              ? ranges.second
              : ranges.first.value;
          const second =
            ranges.second > ranges.first.value
              ? ranges.second
              : ranges.first.value;

          const selectedNodes = this.showValues.filter(
            (item, index) => index > first && index < second
          );

          selectedNodes.forEach(item => {
            item.state.checked = ranges.first.state;
            this.syncNestedValues(item, ranges.first.state);
            this.syncParentValues();
          });
        }
      }

      const debouncedEmit = debounce(this.emitInput, 200);
      debouncedEmit();
    },
    syncNestedValues(node, newState) {
      if (!node.values) return;

      const sync = data => {
        for (let i = 0; data.length > i; i++) {
          data[i].state.checked = newState;
          if (data[i].values) sync(data[i].values);
        }

        return data;
      };

      return sync(node.values);
    },
    syncParentValues() {
      const sync = tree => {
        const indeterminate = new Set();
        let checkedCount = 0;
        for (let i = 0; tree.values.length > i; i++) {
          indeterminate.add(tree.values[i].state.checked);
          if (tree.values[i].values) sync(tree.values[i]);
          if (tree.values[i].state.checked) checkedCount++;
          if (
            tree.values[i].state.indeterminate ||
            tree.values[i].state.checked
          ) {
            // если у вложеного состояние неопределенности, то и родителю ставим такое
            indeterminate.add(false);
            indeterminate.add(true);
          }
        }

        tree.state.checked = checkedCount === tree.values.length;
        tree.state.indeterminate =
          indeterminate.size === 2 && !tree.state.checked;
      };

      sync({ values: this.preparedValues, state: {} });
    },
    prepareData() {
      const clonedValues = cloneDeep(this.options);
      const defaultState = {
        checked: false,
        expanded: false,
        indeterminate: false
      };

      const prepare = data => {
        for (let i = 0; data.length > i; i++) {
          this.$set(
            data[i],
            'state',
            data[i].state
              ? { ...defaultState, ...data[i].state }
              : { ...defaultState }
          );
          if (data[i].values) prepare(data[i].values);
        }
        return data;
      };

      return prepare(clonedValues);
    },

    onKeyDown(e) {
      this.range = e.shiftKey;
    },

    onKeyUp(e) {
      this.range = e.shiftKey;
      this.ranges.first.value = null;
      this.ranges.first.state = null;
      this.ranges.second = null;
    }
  },

  created() {
    window.removeEventListener('keydown', this.onKeyDown);
    window.addEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.addEventListener('keyup', this.onKeyUp);
    this.init();
  },

  watch: {
    options: {
      deep: true,
      handler(val) {
        if (isEqual(this.currentOptions, val)) return;
        this.init();
        this.currentOptions = cloneDeep(val);
      }
    },
    value: {
      deep: true,
      handler(val) {
        if (isEqual(this.currentValue, val)) return;
        this.currentValue = cloneDeep(val);
        this.initValues();
      }
    }
  }
};
</script>
